import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessage, apiErrorDetails } from '../api/apiError';

/*
 * Modal popup for a failed API call — errors no longer sit scattered
 * in the document. The headline is the frontend's own translated
 * message derived from the HTTP status; the backend's technical
 * message appears only once the details are expanded (its texts are
 * not reliably translated, see requirements/backend_requirements.md
 * BE-9), together with the error id for the backend log as soon as
 * the backend sends one (BE-10).
 *
 * A native <dialog> opened modally, like ConfirmDialog: it traps the
 * focus, closes on Escape and dims the page behind it. jsdom knows
 * neither showModal() nor close(), hence the guards. When several
 * calls fail at once (typically: server down), only the first dialog
 * opens and speaks for all of them — the others close themselves
 * straight away and leave their inline remnant behind.
 */
export function ErrorDialog({ error, fallbackKey, onClose, onRetry }) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const other = document.querySelector('dialog.error-dialog[open]');
    if (other && other !== dialog) {
      onClose();
      return undefined;
    }

    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    /* Escape and the backdrop close the native dialog */
    const handleCancel = (event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  const details = apiErrorDetails(error);

  return (
    <dialog
      ref={dialogRef}
      className="error-dialog"
      aria-label={t('common.error')}
    >
      <h2>{t('common.error')}</h2>
      <p role="alert" className="error-dialog-message">
        {apiErrorMessage(error, t, { fallbackKey })}
      </p>

      <details className="error-dialog-details">
        <summary>{t('error.details')}</summary>
        {details.status != null && <p>HTTP {details.status}</p>}
        {details.errorId && (
          <p>
            {t('error.id')}: {details.errorId}
          </p>
        )}
        {details.backendMessage && <p>{details.backendMessage}</p>}
        {details.transportMessage && <p>{details.transportMessage}</p>}
      </details>

      <div className="form-actions">
        {onRetry && (
          <button
            type="button"
            onClick={() => {
              onClose();
              onRetry();
            }}
          >
            {t('error.retry')}
          </button>
        )}
        <button type="button" className="btn-cancel" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </dialog>
  );
}
